/**
 * /burn-subtitles and /add-subtitles-to-video: a video and a subtitle file in,
 * a video with the subtitles drawn into every frame out. Everything happens in
 * this tab; the video is never uploaded.
 *
 * The page loads this bundle beside boot.js (the shared shell). It owns the
 * drop zone, the preview, the style controls and the encoding worker
 * (burn-worker.ts), which is where mediabunny and WebCodecs live, so the
 * shared bundle every other page downloads carries none of it.
 *
 * The preview is a frame decoded by the same worker code that burns (one
 * worker per dropped video, kept open so a scrub is a seek, not a reopen),
 * drawn here with the same renderer at the video's own size, so what the
 * preview shows is what the saved file shows. A <video> element would not do:
 * it cannot play an MKV in Safari, which WebCodecs decodes fine.
 *
 * GENERATING SUBTITLES (the "from the speech" option on
 * /add-subtitles-to-video) is a link (server/app/burn_pages.py picks its
 * target). A page that makes subtitles itself hands them, and the video, to
 * this bundle by event on the `[data-burn-page]` root:
 *   root.dispatchEvent(new CustomEvent('burn:video', {detail: {file}}))
 *   root.dispatchEvent(new CustomEvent('burn:subtitles', {detail: {bytes, name}}))
 * which go through `setVideo` and `loadSubtitleBytes`, the same paths as a
 * dropped file. The auto subtitle generator (pages/autosub.ts) does this, and
 * its burn section has no drop zone of its own, so the drop zone is optional.
 *
 * THE USAGE COUNTER (base/src/lib/usage.ts), under the root's
 * `data-usage-tool` slug: "loaded" once per video when it and the subtitles are
 * both ready, "failed" for a video or subtitle file that cannot be read (or a
 * file that is neither), and "downloaded" for each saved video, in the format
 * it was saved in. Where the files came by event instead of from this bundle's
 * own drop zone, the page that sent them counts them, so only the saved video
 * is counted here.
 */

import type { SubtitleDocument } from '../../../../contracts/cue-model.ts';
import {
  classify,
  extensionOf,
  mountDropzone,
  videoTypeName,
} from '../../base/src/lib/dropzone.ts';
import { mediaFormat, usageCounter } from '../../base/src/lib/usage.ts';
import { burnedName, canBurnHere, type OutputPlan } from './lib/codec.ts';
import { createRenderer, type SubtitleRenderer } from './lib/draw.ts';
import {
  buildTrack,
  DEFAULT_PLAIN,
  isStyledFormat,
  type PlainOptions,
  type RenderTrack,
} from './lib/model.ts';
import type { BurnFailure, FromWorker, Probe, ToWorker } from './lib/protocol.ts';
import { readSubtitles } from './lib/read.ts';

type ErrorKey =
  | BurnFailure
  | 'subs-unreadable'
  | 'subs-empty'
  | 'wrong-file'
  | 'audio-file'
  | 'video-format';

function workerUrl(): string {
  const build = document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim();
  return build
    ? `/static/build/burn/burn-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/burn/burn-worker.js';
}

function q<T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking in the same task cancels the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function mount(root: HTMLElement): void {
  const unsupported = q(document, '[data-burn-unsupported]');
  const tool = q(root, '[data-burn-tool]');
  if (!canBurnHere(globalThis as unknown as Record<string, unknown>)) {
    if (unsupported) unsupported.hidden = false;
    if (tool) tool.hidden = true;
    return;
  }

  const files = q(root, '[data-burn-files]');
  const videoSlot = q(root, '[data-burn-files] [data-slot="video"]');
  const subsSlot = q(root, '[data-burn-files] [data-slot="subs"]');
  const style = q(root, '[data-burn-style]');
  const assNote = q(root, '[data-burn-ass-note]');
  const preview = q(root, '[data-burn-preview]');
  const canvas = q<HTMLCanvasElement>(root, '[data-burn-canvas]');
  const scrub = q<HTMLInputElement>(root, '[data-burn-scrub]');
  const timeSlot = q(root, '[data-burn-preview] [data-slot="time"]');
  const save = q<HTMLButtonElement>(root, '[data-burn-save]');
  const working = q(root, '[data-burn-working]');
  const fill = q(root, '[data-burn-fill]');
  const pctSlot = q(root, '[data-burn-working] [data-slot="pct"]');
  const cancel = q<HTMLButtonElement>(root, '[data-burn-cancel]');
  const status = q(root, '[data-burn-status]');
  const host = q(root, '[data-dropzone-host]');
  if (!canvas || !scrub || !save) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const label = (key: string): string =>
    q(root, `[data-burn-label="${key}"]`)?.textContent?.trim() ?? '';

  // Counting needs a slug; the inputs are counted only when they came through this drop zone.
  const usageTool = root.dataset['usageTool'] ?? '';
  const usage = usageTool ? usageCounter(usageTool) : null;
  const inputs = host ? usage : null;
  const videoFormat = (file: File) => mediaFormat(extensionOf(file.name), 'video');

  let video: File | null = null;
  let probe: Probe | null = null;
  let subsName = '';
  let doc: SubtitleDocument | null = null;
  let opts: PlainOptions = DEFAULT_PLAIN;
  let track: RenderTrack | null = null;
  let renderer: SubtitleRenderer | null = null;
  let job: Worker | null = null;
  let previewer: Worker | null = null;

  /* ── messages ─────────────────────────────────────────────────────────── */

  const showError = (key: ErrorKey | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-burn-error]')) {
      el.hidden = el.dataset['burnError'] !== key;
    }
  };
  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };

  /* ── preview ──────────────────────────────────────────────────────────── */

  // The last decoded frame and its timestamp, redrawn when a control changes.
  let frame: ImageBitmap | null = null;
  let frameTime = 0;
  // One request in flight; a scrub while it decodes replaces the queued one.
  let frameId = 0;
  let inFlight = false;
  let queued: number | null = null;

  const drawPreview = () => {
    if (!frame) return;
    ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
    renderer?.draw(ctx, { width: canvas.width, height: canvas.height }, frameTime);
    // Tenths, so stepping through a cue's start shows which side of it this is.
    if (timeSlot) timeSlot.textContent = `${clock(frameTime)}.${Math.floor((frameTime % 1) * 10)}`;
  };

  const requestFrame = (t: number) => {
    if (!previewer) return;
    if (inFlight) {
      queued = t;
      return;
    }
    inFlight = true;
    frameId += 1;
    previewer.postMessage({ type: 'frame', id: frameId, time: t } satisfies ToWorker);
  };

  const seek = (t: number) => {
    const duration = probe?.duration ?? 0;
    const to = Math.max(0, duration > 0 ? Math.min(t, Math.max(0, duration - 0.05)) : t);
    scrub.value = String(to);
    requestFrame(to);
  };

  /** Somewhere the first subtitle shows, so the preview has one on it. */
  const firstCueTime = () => {
    const first = track?.cues[0];
    return first ? first.start + Math.min(0.5, (first.end - first.start) / 2) : 0;
  };

  scrub.addEventListener('input', () => seek(Number(scrub.value)));

  /* ── state ────────────────────────────────────────────────────────────── */

  const ready = () => video !== null && probe !== null && doc !== null && track !== null;
  const refreshSave = () => {
    save.disabled = !ready() || job !== null;
    // Once per video: new subtitles for the same video, or a style change, are not a new use.
    if (video && ready()) inputs?.loaded(video, videoFormat(video));
  };

  const describeVideo = () => {
    if (!videoSlot || !video) return;
    videoSlot.textContent = probe
      ? `${video.name} · ${probe.width} × ${probe.height} · ${clock(probe.duration)}`
      : `${video.name} · ${label('checking')}`;
  };

  const rebuild = () => {
    if (!doc) return;
    track = buildTrack(doc, opts);
    renderer = createRenderer(track);
    drawPreview();
    refreshSave();
  };

  const readControls = (): PlainOptions => {
    const value = (name: string) =>
      q<HTMLInputElement | HTMLSelectElement>(root, `[data-burn-control="${name}"]`)?.value;
    const size = Number(value('sizePct'));
    const outline = value('outline');
    const position = value('position');
    return {
      sizePct: Number.isFinite(size) && size > 0 ? size : DEFAULT_PLAIN.sizePct,
      color: value('color') || DEFAULT_PLAIN.color,
      outlineColor: value('outlineColor') || DEFAULT_PLAIN.outlineColor,
      outline: outline === 'none' || outline === 'thick' ? outline : 'thin',
      position: position === 'middle' || position === 'top' ? position : 'bottom',
    };
  };
  for (const control of root.querySelectorAll('[data-burn-control]')) {
    control.addEventListener('input', () => {
      opts = readControls();
      rebuild();
    });
    control.addEventListener('change', () => {
      opts = readControls();
      rebuild();
    });
  }

  /* ── files ────────────────────────────────────────────────────────────── */

  const setVideo = (file: File) => {
    showError(null);
    setStatus('');
    video = file;
    probe = null;
    frame?.close();
    frame = null;
    if (preview) preview.hidden = true;
    if (files) files.hidden = false;
    describeVideo();
    refreshSave();

    previewer?.terminate();
    inFlight = false;
    queued = null;
    const w = new Worker(workerUrl(), { type: 'module' });
    previewer = w;
    w.onmessage = (ev: MessageEvent<FromWorker>) => {
      const msg = ev.data;
      if (previewer !== w) return;
      if (msg.type === 'probed') {
        probe = msg.probe;
        canvas.width = probe.width;
        canvas.height = probe.height;
        scrub.max = String(probe.duration);
        describeVideo();
        refreshSave();
        seek(firstCueTime());
      } else if (msg.type === 'frame') {
        inFlight = false;
        if (msg.id === frameId) {
          frame?.close();
          frame = msg.bitmap;
          frameTime = msg.time;
          if (preview) preview.hidden = false;
          drawPreview();
        } else {
          msg.bitmap.close();
        }
        if (queued !== null) {
          const next = queued;
          queued = null;
          requestFrame(next);
        }
      } else if (msg.type === 'failed') {
        showError(msg.reason);
        inputs?.failed(file, videoFormat(file));
        if (videoSlot) videoSlot.textContent = file.name;
        w.terminate();
        previewer = null;
      }
    };
    w.onerror = () => {
      if (previewer !== w) return;
      showError('failed');
      inputs?.failed(file, videoFormat(file));
      w.terminate();
      previewer = null;
    };
    w.postMessage({ type: 'probe', file } satisfies ToWorker);
  };

  /**
   * Also the hook a speech-to-subtitles step can call with the SRT it produced. `counted` is the
   * dropped file, when there is one: what a subtitle file that cannot be read is counted as.
   */
  const loadSubtitleBytes = (bytes: Uint8Array, name: string, counted?: File) => {
    const outcome = readSubtitles(bytes, name);
    if (!outcome.ok) {
      showError(outcome.reason === 'empty' ? 'subs-empty' : 'subs-unreadable');
      if (counted) inputs?.failed(counted, extensionOf(counted.name));
      return;
    }
    showError(null);
    setStatus('');
    doc = outcome.doc;
    subsName = name;
    const styled = isStyledFormat(outcome.doc);
    if (style) style.hidden = styled;
    if (assNote) assNote.hidden = !styled;
    if (files) files.hidden = false;
    rebuild();
    if (subsSlot && track) {
      subsSlot.textContent = `${subsName} · ${track.cues.length} ${label(track.cues.length === 1 ? 'one-subtitle' : 'subtitles')}`;
    }
    if (probe) seek(firstCueTime());
  };

  const setSubs = async (file: File) => {
    loadSubtitleBytes(new Uint8Array(await file.arrayBuffer()), file.name, file);
  };

  root.addEventListener('burn:subtitles', (ev) => {
    const detail = (ev as CustomEvent<{ bytes?: unknown; name?: unknown }>).detail;
    if (job || !detail || !(detail.bytes instanceof Uint8Array)) return;
    loadSubtitleBytes(
      detail.bytes,
      typeof detail.name === 'string' ? detail.name : 'subtitles.srt',
    );
  });

  // The video, from a page that already has it (the auto subtitle generator).
  root.addEventListener('burn:video', (ev) => {
    const detail = (ev as CustomEvent<{ file?: unknown }>).detail;
    if (job || !detail || !(detail.file instanceof File)) return;
    // The same video again (the generator re-sends it with every edit) keeps its preview.
    if (detail.file === video) return;
    setVideo(detail.file);
  });

  if (host)
    mountDropzone(host, {
      onFiles: (dropped) => {
        if (job) return;
        for (const file of dropped) {
          const kind = classify(file);
          if (kind === 'container') setVideo(file);
          else if (kind === 'subtitle') void setSubs(file);
          else if (kind === 'audio') {
            showError('audio-file');
            inputs?.failed(file, mediaFormat(extensionOf(file.name), 'audio'));
          } else if (kind === 'other-video') {
            // B5_VIDEO_FORMAT's words (tools/_burn_tool.html), with the format filled in.
            for (const slot of root.querySelectorAll<HTMLElement>(
              '[data-burn-error="video-format"] [data-slot="format"]',
            )) {
              slot.textContent = videoTypeName(file.name);
            }
            showError('video-format');
            inputs?.failed(file, 'video');
          } else {
            showError('wrong-file');
            inputs?.failed(file, kind === 'archive' ? 'zip' : 'unknown');
          }
        }
      },
      onText: (text) => {
        if (job) return;
        loadSubtitleBytes(new TextEncoder().encode(text), 'pasted.srt');
      },
    });

  /* ── encoding ─────────────────────────────────────────────────────────── */

  const finish = () => {
    job?.terminate();
    job = null;
    if (working) working.hidden = true;
    refreshSave();
  };

  const showProgress = (fraction: number) => {
    const pct = Math.max(0, Math.min(100, Math.floor(fraction * 100)));
    fill?.style.setProperty('--progress-pct', `${pct}%`);
    if (pctSlot) pctSlot.textContent = String(pct);
  };

  save.addEventListener('click', () => {
    if (!ready() || !video || !track) return;
    const file = video;
    const w = new Worker(workerUrl(), { type: 'module' });
    job = w;
    showError(null);
    setStatus('');
    showProgress(0);
    if (working) working.hidden = false;
    refreshSave();
    w.onmessage = (ev: MessageEvent<FromWorker>) => {
      const msg = ev.data;
      if (job !== w) return;
      if (msg.type === 'progress') {
        showProgress(msg.fraction);
      } else if (msg.type === 'done') {
        const plan: OutputPlan = msg.plan;
        const name = burnedName(file.name, plan);
        saveBlob(new Blob([msg.buffer], { type: plan.mime }), name);
        usage?.downloaded(plan.container);
        finish();
        const parts = [`${label('saved')} ${name}.`];
        if (msg.audio === 'dropped') parts.push(label('audio-dropped'));
        setStatus(parts.join(' '));
      } else if (msg.type === 'failed') {
        finish();
        showError(msg.reason);
      }
    };
    w.onerror = () => {
      if (job !== w) return;
      finish();
      showError('failed');
    };
    w.postMessage({ type: 'burn', file, track } satisfies ToWorker);
  });

  cancel?.addEventListener('click', () => {
    if (!job) return;
    finish();
    setStatus(label('cancelled'));
  });
}

const root = document.querySelector<HTMLElement>('[data-burn-page]');
if (root) mount(root);
